import { Offcanvas, Button, Col, Row } from "react-bootstrap";
import LoggedInLayout from "../../layouts/loggedin-layout";
import { useEffect, useMemo, useState } from "react";
import styles from '../../styles/offcanvasnav.module.css';
import ButtonOverride from "../../components/buttonoverride";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faCircleArrowLeft, faCircleArrowRight } from "@fortawesome/free-solid-svg-icons";
import ClientInformationExpanded from "../../components/clientinformationexpanded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetDemographicsQuery } from "../../new/query";
import useUserStore from "../../stores/user-store";
import { hasAnyPermission, hasPermission, permissionPaths } from "../../auth/permission-paths";
import PermissionReadOnlyBoundary from "../../auth/PermissionReadOnlyBoundary";

const normalizePathname = (pathname = "") => {
    if(pathname.length > 1){
        return pathname.replace(/\/+$/,'').toLowerCase();
    }

    return pathname.toLowerCase();
}

const applicationPageAccess = {
    __index: {
        add: null,
        update: permissionPaths.applicationForm.update,
        delete: null
    },
    screening: {
        add: null,
        update: permissionPaths.screening.update,
        delete: null
    },
    facesheet: {
        add: null,
        update: permissionPaths.faceSheet.update,
        delete: null
    },
    documentupload: {
        add: permissionPaths.documents.add,
        update: null,
        delete: permissionPaths.documents.delete
    },
    costestimate: {
        add: permissionPaths.costEstimate.add,
        update: permissionPaths.costEstimate.update,
        delete: permissionPaths.costEstimate.delete
    },
    transitionplan: {
        add: permissionPaths.transitionPlan.add,
        update: permissionPaths.transitionPlan.update,
        delete: permissionPaths.transitionPlan.delete
    },
    appstatus: {
        add: permissionPaths.applicationHistory.add,
        update: null,
        delete: null
    },
    housing: {
        add: null,
        update: permissionPaths.housing.update,
        delete: null
    },
    transitionchecklist: {
        add: null,
        update: permissionPaths.transitionChecklist.update,
        delete: null
    },
    expenditureworksheet: {
        add: permissionPaths.expenditureWorksheet.add,
        update: permissionPaths.expenditureWorksheet.update,
        delete: permissionPaths.expenditureWorksheet.delete
    },
    pds: {
        add: permissionPaths.participantDirectedServices.add,
        update: permissionPaths.participantDirectedServices.update,
        delete: permissionPaths.participantDirectedServices.delete
    }
};

const noteSegments = new Set(["casemanagementnotes", "noteshousing", "hdinotes"]);

const getCurrentPageAccess = (pathname, applicationId) => {
    const basePath = normalizePathname(`/Client/Applications/${applicationId}`);
    const currentPath = normalizePathname(pathname);

    if(currentPath === basePath){
        return applicationPageAccess.__index;
    }

    if(!currentPath.startsWith(`${basePath}/`)){
        return null;
    }

    const segment = currentPath.slice(basePath.length + 1).split("/")[0];

    if(noteSegments.has(segment) || segment === "history"){
        return null;
    }

    return applicationPageAccess[segment] ?? null;
}

const ClientFormsPage = () => {
    const [open,setOpen] = useState(false);
    const [panel,setPanel] = useState('root');
    const {applicationid: applicationID} = useParams();
    const permissions = useUserStore((state) => state.permissions);

    const navigate = useNavigate();
    const location = useLocation();

    const query = useGetDemographicsQuery(applicationID);
    const currentPageAccess = useMemo(
        () => getCurrentPageAccess(location.pathname, applicationID),
        [location.pathname, applicationID]
    );
    const pageActionPermissions = useMemo(
        () => [
            currentPageAccess?.add,
            currentPageAccess?.update,
            currentPageAccess?.delete
        ].filter(Boolean),
        [currentPageAccess]
    );
    const pagePermissions = useMemo(
        () => ({
            canAdd: hasPermission(permissions, currentPageAccess?.add),
            canUpdate: hasPermission(permissions, currentPageAccess?.update),
            canDelete: hasPermission(permissions, currentPageAccess?.delete),
            canModify: pageActionPermissions.length > 0 && hasAnyPermission(permissions, pageActionPermissions)
        }),
        [currentPageAccess, pageActionPermissions, permissions]
    );
    const currentPageReadOnly = pageActionPermissions.length > 0 && !pagePermissions.canModify;


    const sections = useMemo(
        () => [
            {
                key: 'primary',
                label: 'Primary',
                children: [
                    {
                        key: 'home',
                        title: 'Home',
                        to: () => `/Client/Client/${query.data.medicaidID}`,
                        permission: permissionPaths.clientApplications.display,
                        match: (pathname,applicationid) => false //Will never be true because it navigates away
                    },
                    {key: 'application',title: 'Referral/Applications',to: (applicationid) => `/Client/Applications/${applicationid}`,exact: true, permission: permissionPaths.applicationForm.display},
                    {key: 'screening',title: 'Screening',to: (applicationid) => `/Client/Applications/${applicationid}/screening`, permission: permissionPaths.screening.display},
                    {key: 'facesheet',title: 'Face Sheet',to: (applicationid) => `/Client/Applications/${applicationid}/facesheet`, permission: permissionPaths.faceSheet.display}
                ]
            },
            {
                key: 'documentupload',
                label: 'Document Upload',
                children: [
                    {key: 'documentupload',title: 'Document Upload',to: (applicationid) => `/Client/Applications/${applicationid}/documentupload`, permission: permissionPaths.documents.display}
                ]
            },
            {
                key: 'transition',
                label: 'Transition',
                children: [
                    {key: 'costestimate',title: 'Cost Estimate',to: (applicationid) => `/Client/Applications/${applicationid}/costestimate`, permission: permissionPaths.costEstimate.display},
                    {key: 'transitionplan',title: 'Transition Plan',to: (applicationid) => `/Client/Applications/${applicationid}/transitionplan`, permission: permissionPaths.transitionPlan.display},
                    {key: 'changeappstatus',title: 'Change Application Status',to: (applicationid) => `/Client/Applications/${applicationid}/appstatus`, permission: permissionPaths.applicationHistory.add},
                    {key: 'housing',title: 'Housing',to: (applicationid) => `/Client/Applications/${applicationid}/housing`, permission: permissionPaths.housing.display},
                    {key: 'transitionchecklist',title: 'Transition Checklist',to: (applicationid) => `/Client/Applications/${applicationid}/transitionchecklist`, permission: permissionPaths.transitionChecklist.display},
                    {key: 'history',title: 'History',to: (applicationid) => `/Client/Applications/${applicationid}/history`, permission: permissionPaths.applicationHistory.display},
                    {key: 'expenditureworksheet',title: 'Expenditure Worksheet',to: (applicationid) => `/Client/Applications/${applicationid}/expenditureworksheet`, permission: permissionPaths.expenditureWorksheet.display}
                ]
            },
            {
                key: 'notes',
                label: 'Notes',
                children: [
                    {key: 'casemanagementnotes',title: 'Case Management Notes',to: (applicationid) => `/Client/Applications/${applicationid}/casemanagementnotes`, permission: permissionPaths.notes.caseManagementDisplay},
                    {key: 'housingnotes',title: 'Housing Notes',to: (applicationid) => `/Client/Applications/${applicationid}/noteshousing`, permission: permissionPaths.notes.housingDisplay},
                    {key: 'hdinotes',title: 'HDI Notes',to: (applicationid) => `/Client/Applications/${applicationid}/hdinotes`, permission: permissionPaths.notes.hdiDisplay}
                ]
            },
            {
                key: 'misc',
                label: 'Misc',
                children: [
                    {key: 'pds',title: 'Participant Directed Services',to: (applicationid) => `/Client/Applications/${applicationid}/pds`, permission: permissionPaths.participantDirectedServices.display}
                ]
            }
        ]
            .map(section => ({
                ...section,
                children: section.children.filter(child => hasPermission(permissions, child.permission))
            }))
            .filter(section => section.children.length > 0),
        [query.data, permissions]
    );

    const childIsActive = (child) => {
        if(child.match) return child.match(location.pathname,applicationID);
        const target = typeof child.to === 'function' ? child.to(applicationID) : child.to;
        const currentPath = normalizePathname(location.pathname);
        const targetPath = normalizePathname(target);

        if(child.exact){
            return currentPath === targetPath;
        }

        return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
    }
    const sectionIsActive = (section) => section.children.some(childIsActive);
    const activeSection = sections.find(sectionIsActive) ?? null;

    const go = (to) => {
        navigate(typeof to === 'function' ? to(applicationID) : to);
        setOpen(false);
    }

    useEffect(() => {
        setPanel(activeSection ? activeSection.key : 'root');
    },[activeSection])

    const handleOpen = () => {
        setPanel(activeSection ? activeSection.key : 'root');
        setOpen(true);
    }

    const currentPanel = panel === 'root' ? null : sections.find(section => section.key === panel);

    if(query.isLoading) return <div>loading...</div>

    return (
            <LoggedInLayout>
                <ButtonOverride type="button" title="Open Navigation" aria-label="Open Navigation" className={` ${styles.openOffcanvasBtn} ${styles.removepadding} `} onClick={handleOpen}>
                    <FontAwesomeIcon icon={faBars} size="xl" />
                </ButtonOverride>
                <div style={{ padding: "25px" }}>
                    <Row>
                        <Col xs={12} lg={3}>
                            <ClientInformationExpanded ApplicationId={applicationID} />
                        </Col>
                        <Col xs={12} lg={9}>
                            <main>
                                <PermissionReadOnlyBoundary readOnly={currentPageReadOnly}>
                                    <Outlet context={{ApplicationID: applicationID, pagePermissions, currentPageReadOnly}}/>
                                </PermissionReadOnlyBoundary>
                            </main>
                        </Col>
                    </Row>
                </div>
                <Offcanvas show={open} onHide={() => setOpen(false)}>
                    <Offcanvas.Header className={styles.navHeader} closeButton>
                        <Offcanvas.Title>{panel === 'root' ? 'Navigation' : currentPanel?.label}</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <nav className={styles.NavContainer}>
                            {
                                panel === 'root'
                                ?
                                    sections.map(section => (
                                        <ButtonOverride key={section.key} className={`${styles.topBtn} ${sectionIsActive(section) ? styles.active : ''} mt-3`} type="button" onClick={() => setPanel(section.key)}>
                                            <span>{section.label}</span>
                                            <FontAwesomeIcon className={styles.navIcon} icon={faCircleArrowRight} size="xl" />
                                        </ButtonOverride>
                                    ))
                                :
                                    currentPanel?.children.map(child => (
                                        <ButtonOverride key={child.key} className={`${styles.resultItem} ${childIsActive(child) ? styles.active : ''}`} type="button" onClick={() => go(child.to)}>{child.title}</ButtonOverride>
                                    ))
                            }
                        </nav>
                        <div className={styles.NavFooter}>
                            {
                                    panel !== 'root'
                                    ?
                                    <ButtonOverride className={`${styles.returnBtn} ${styles.back}`} type="button" onClick={() => setPanel('root')}>
                                        <FontAwesomeIcon icon={faCircleArrowLeft} size="2xl" />
                                    </ButtonOverride>
                                    :
                                    <Button variant="link" type="button" className={`${styles.returnLink} ${styles.removepadding}`} onClick={() => navigate(`/Client/ClientSearch`, { state: { restoreClientSearch: true } })}>Return to Client Search</Button>
                            }

                        </div>
                    </Offcanvas.Body>
                </Offcanvas>
            </LoggedInLayout>
    )
}

export default ClientFormsPage;
